/**
 * The admin home page: what is waiting, what is ageing, and what the chosen range produced.
 *
 * ## What this replaced, and why
 *
 * It used to lead with **Daily Traffic** (a line chart) and a **Conversion Funnel**, both built
 * from `analytics_events` — a table of HTTP requests. So "traffic" measured how often the React app
 * called the API, and the funnel was derived from request *paths* rather than from what the cases
 * actually did. Neither number changed a decision: knowing Tuesday had more requests than Monday
 * tells an office that drafts legal documents nothing it can act on.
 *
 * Everything here comes from the transactions and payments tables — the record of work and money —
 * and every figure links to the queue that resolves it. A dashboard should be a to-do list with
 * numbers on it, not a chart of the web server.
 *
 * ## Colour
 *
 * **Green is good, red is a delay.** For work waiting on this office, *zero is the good state* — an
 * empty queue means nothing is stuck — so those cards are green at 0 and red above it. For output
 * (money collected, documents delivered) the polarity is the opposite and more is better. Cards
 * declare which they are; guessing from the label would get it wrong exactly once, and it would be
 * the money.
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowRight, Clock } from "lucide-react";

import { api } from "@/lib/apiClient";
import { fmtDate } from "@/lib/dates";
import { ALL_TIME, DateRange, isoToday, useDateRange } from "@/components/DateRange";

/** Days between now and `iso`, or null. */
function daysSince(iso) {
  if (!iso) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
}

/**
 * A number that is also a destination, coloured by what it means.
 *
 *   `todo` — waiting on admin. 0 is green (clear), anything above is red (delayed).
 *   `good` — output. Always green; more is better.
 *   `info` — context, never a verdict, never coloured.
 */
const ActionCard = ({ label, value, hint, to, tone = "todo", testid }) => {
  const nav = useNavigate();
  const n = typeof value === "number" ? value : null;
  const state = tone === "good" ? "good" : tone === "info" ? "info" : n === 0 ? "good" : "bad";
  const ring = state === "good" ? "border-[#10B981]/35 bg-[#10B981]/[0.05]"
    : state === "bad" ? "border-[#F27373]/40 bg-[#F27373]/[0.05]"
    : "border-white/10";
  const num = state === "good" ? "text-[#10B981]"
    : state === "bad" ? "text-[#F27373]" : "text-white";
  return (
    <button
      type="button" onClick={() => to && nav(to)} data-testid={testid} data-tone={state}
      className={`text-left rounded-2xl border ${ring} bg-[#111623] p-5 transition-colors
                  ${to ? "hover:border-[#CBA96C]/60 cursor-pointer" : "cursor-default"}`}
    >
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6B7280]">{label}</div>
      <div className={`font-serif-display text-3xl mt-1 ${num}`}>{value}</div>
      {hint && (
        <div className="text-[11px] text-[#6B7280] mt-1.5 flex items-center gap-1">
          {hint}{to && <ArrowRight className="w-3 h-3 opacity-60" />}
        </div>
      )}
    </button>
  );
};

const Panel = ({ title, children, testid }) => (
  <section className="rounded-2xl border border-white/10 bg-[#111623] overflow-hidden" data-testid={testid}>
    <div className="px-5 py-3 border-b border-white/5 text-[10px] tracking-[0.24em] uppercase text-[#6B7280]">
      {title}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

export function AdminAnalytics() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  // The window comes from Admin → Settings (`default_range_days`) unless the URL carries one, so
  // this page, the transaction queues and the payments ledger all open on the same span. Sent on
  // every fetch, so the server and the page can never disagree about which window is on screen.
  const { from, to, ready, setRange } = useDateRange(params);

  // Mirror the window into the URL so a view is a shareable link and so the browser's Back button
  // returns to the range you were looking at rather than the default. `replace`, because dragging
  // a date picker across a week should not leave seven entries in the history.
  useEffect(() => {
    if (!ready) return;
    if (params.get("from") === from && params.get("to") === to) return;
    const next = new URLSearchParams(params);
    next.set("from", from); next.set("to", to);
    setParams(next, { replace: true });
  }, [ready, from, to, params, setParams]);

  useEffect(() => {
    if (!ready) return;
    let alive = true;
    api.get("/admin/analytics", { params: { from, to } })
      .then(r => { if (alive) setD(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [ready, from, to]);

  // Every card is a link into the queue or ledger that resolves it, and the link carries the
  // window — clicking "3 unassigned" and landing on a list filtered to a different span is how a
  // number stops being trustworthy. `q()` appends it to a queue path; `allTime()` is for the two
  // ageing cards, which are all-time figures: sending a 7-day window with "older than 7 days"
  // would open a queue that is necessarily empty.
  const rangeQs = ready ? `from=${from}&to=${to}` : "";
  const q = (path) => (ready ? `${path}${path.includes("?") ? "&" : "?"}${rangeQs}` : path);
  const allTime = (path) =>
    `${path}${path.includes("?") ? "&" : "?"}from=${ALL_TIME}&to=${isoToday()}`;

  const range = <DateRange from={from} to={to} onChange={setRange} testid="dash-range" />;

  if (!d) {
    return (
      <div className="space-y-6" data-testid="admin-analytics">
        <h1 className="font-serif-display text-3xl md:text-4xl text-white">Dashboard</h1>
        {range}
        <div className="text-sm text-[#6B7280]">Loading…</div>
      </div>
    );
  }

  const a = d.attention || {};
  const ag = d.aging || {};
  const pr = d.period || {};
  const stale = ag.stale_after_days ?? 7;
  const oldestDays = daysSince(ag.oldest);

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <h1 className="font-serif-display text-3xl md:text-4xl text-white">Dashboard</h1>
        <span className={`text-sm ${a.total > 0 ? "text-[#F27373]" : "text-[#10B981]"}`}
              data-testid="attention-total">
          {a.total > 0
            ? `${a.total} case${a.total === 1 ? "" : "s"} waiting on you`
            : "Nothing waiting on you"}
        </span>
      </div>

      {range}
      {/* No explanatory paragraph here. Which panels obey the date range is said by the panel
          titles themselves — "In this range" and "Ageing (all time)" — and a sentence restating it
          under the picker was reading the interface aloud. */}

      {/* 1 — the work that has stopped and is waiting on this office */}
      <Panel title="Needs you" testid="panel-attention">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <ActionCard label="Unassigned" value={a.unassigned ?? 0} testid="attn-unassigned"
                      hint="Paid, nobody reviewing yet" to={q("/admin/transactions?tab=admin")} />
          <ActionCard label="To issue" value={a.to_issue ?? 0} testid="attn-issue"
                      hint="Review accepted, soft copy not uploaded" to={q("/admin/transactions?tab=admin")} />
          <ActionCard label="To dispatch" value={a.to_dispatch ?? 0} testid="attn-dispatch"
                      hint="Issued, not yet couriered" to={q("/admin/transactions?tab=delivery")} />
        </div>
        {/* Assigned work is in flight, not overdue — informational, so never red. Counting it as a
            to-do would keep the headline permanently large and therefore ignorable. */}
        <div className="grid grid-cols-2 gap-3 mt-3">
          <ActionCard label="Assigned — admin" value={a.assigned_admin ?? 0} tone="info"
                      testid="attn-assigned-admin" hint="Admin is reviewing"
                      to={q("/admin/transactions?tab=admin")} />
          <ActionCard label="Assigned — advocate" value={a.assigned_adv ?? 0} tone="info"
                      testid="attn-assigned-adv" hint="With an advocate"
                      to={q("/admin/transactions?tab=reviewer")} />
        </div>
      </Panel>

      {/* 2 — what has been waiting too long, regardless of the range.
             Windowing this was a bug: with a two-day range selected, "older than 7 days" counted
             only cases created in those two days — none of which can be seven days old — so it read
             zero and went green while stale cases sat just outside the window. */}
      <Panel title="Ageing (all time)" testid="panel-aging">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <ActionCard label={`Older than ${stale} days`} value={ag.stale ?? 0} testid="aging-stale"
                      hint="Paid and still open" to={allTime("/admin/transactions?tab=pending")} />
          <ActionCard label="Open cases" value={ag.open_total ?? 0} tone="info" testid="aging-open"
                      hint="Paid, not yet closed" to={allTime("/admin/transactions?tab=pending")} />
          <div className={`rounded-2xl border p-5 bg-[#111623] ${
                 oldestDays == null ? "border-white/10"
                 : oldestDays > stale ? "border-[#F27373]/40 bg-[#F27373]/[0.05]"
                 : "border-[#10B981]/35 bg-[#10B981]/[0.05]"}`}
               data-testid="aging-oldest">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#6B7280]">Oldest open case</div>
            {ag.oldest ? (
              <>
                <div className={`font-serif-display text-3xl mt-1 ${
                       oldestDays > stale ? "text-[#F27373]" : "text-[#10B981]"}`}>
                  {oldestDays}<span className="text-lg text-[#6B7280] ml-1">d</span>
                </div>
                <div className="text-[11px] text-[#6B7280] mt-1.5 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> since {fmtDate(ag.oldest)}
                </div>
              </>
            ) : (
              <div className="font-serif-display text-2xl text-[#10B981] mt-1">Nothing open</div>
            )}
          </div>
        </div>
      </Panel>

      {/* 3 — what the chosen range produced */}
      <Panel title="In this range" testid="panel-week">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <ActionCard label="Collected" value={`₹${Number(pr.collected || 0).toLocaleString()}`}
                      tone="good" hint={`${pr.payment_count ?? 0} payments`}
                      to={q("/admin/payments")} testid="week-collected" />
          <ActionCard label="Delivered" value={pr.delivered ?? 0} tone="good"
                      hint="Documents reached the customer"
                      to={q("/admin/transactions?tab=closed")} testid="week-delivered" />
          <ActionCard label="New cases" value={pr.new_cases ?? 0} tone="good"
                      hint="Started in this range" to={q("/admin/transactions")} testid="week-new" />
          <ActionCard label="Avg per payment"
                      value={`₹${pr.payment_count ? Math.round(pr.collected / pr.payment_count).toLocaleString() : 0}`}
                      tone="good" hint="Revenue ÷ payments in range"
                      to={q("/admin/payments")} testid="revenue-avg" />
        </div>
      </Panel>

      {/* 3b — which documents this range actually produced, not just how many. "New cases" above
          answers "how much work"; this answers "of what kind" — the two are read together, so it
          shares that card's exact window rather than opening its own. */}
      <Panel title="Documents in this range" testid="panel-by-document">
        {(() => {
          // The backend already only groups over cases that exist in the window (a plain SQL
          // GROUP BY can't produce a zero-count group), but filtering here too costs nothing and
          // means this panel can never show a document type with nothing behind it, however that
          // ever happened — a defensive check, not a fix for a bug this data can actually have.
          const rows = (d.by_document || []).filter(r => r.count > 0);
          if (rows.length === 0) {
            return <div className="text-sm text-[#6B7280]">No documents started in this range.</div>;
          }
          // Widest bar is the top row's own count, not a fixed scale — the point is comparing
          // rows on this page against each other, not against a number from a different range.
          const top = Math.max(...rows.map(r => r.count), 1);
          return (
            <div className="space-y-2">
              {rows.map(row => (
                <div key={row.key} data-testid={`by-doc-${row.key}`} className="flex items-center gap-3">
                  <span className="text-sm text-white truncate flex-1 min-w-0">{row.title}</span>
                  <div className="w-24 sm:w-36 h-1.5 rounded-full bg-white/5 overflow-hidden shrink-0">
                    <div className="h-full bg-[#CBA96C] rounded-full" style={{ width: `${(row.count / top) * 100}%` }} />
                  </div>
                  <span className="text-sm font-serif-display text-white w-8 text-right shrink-0">{row.count}</span>
                </div>
              ))}
            </div>
          );
        })()}
      </Panel>

      {/* 4 — who is carrying what, so reassignment is an informed decision */}
      <Panel title="Advocate load" testid="panel-reviewers">
        {(d.reviewer_load || []).length === 0 ? (
          <div className="text-sm text-[#6B7280]">No advocates yet.</div>
        ) : (
          <div className="space-y-2">
            {(d.reviewer_load || []).map(r => (
              <button
                key={r.id} type="button" onClick={() => nav(`/admin/reviewers/${r.id}`)}
                data-testid={`load-${r.id}`}
                className="w-full flex items-center justify-between gap-4 rounded-xl border border-white/10
                           bg-[#0B111C] px-4 py-3 hover:border-[#CBA96C]/40 transition-colors text-left"
              >
                <span className="text-sm text-white truncate">{r.name}</span>
                <span className="text-xs text-[#A0AABF] shrink-0">
                  <b className="text-white font-serif-display text-base">{r.open}</b> open
                  <span className="text-[#6B7280]"> · {r.assigned} total</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </Panel>

      {/* Failures are rare and worth surfacing when they are not zero, but they do not deserve a
          card of their own on a page about work in progress. */}
      {d.failed > 0 && (
        <button type="button" onClick={() => nav("/admin/ledger")} data-testid="failed-ledger-link"
                className="w-full flex items-center gap-2 rounded-xl border border-[#F27373]/30
                           bg-[#F27373]/[0.04] px-4 py-3 text-sm text-[#F27373] hover:border-[#F27373]/60
                           transition-colors">
          <AlertTriangle className="w-4 h-4" />
          {d.failed} failed transaction{d.failed === 1 ? "" : "s"} in the ledger
          <ArrowRight className="w-3.5 h-3.5 ml-auto" />
        </button>
      )}
    </div>
  );
}

export default AdminAnalytics;
